'use client';

import { cn } from '../../../../lib/utils';
import { cva } from 'class-variance-authority';
import React from 'react';

export interface SegmentedContextValue {
  value?: string;
  size?: 'sm' | 'md' | 'lg';
  onValueChange?: (value: string) => void;
  keepMounted?: boolean;
  registerSegment?: (value: string, element: HTMLElement) => void;
  unregisterSegment?: (value: string) => void;
  segmentElements?: Map<string, HTMLElement>;
  isShowBgIndicator?: boolean;
  isBgIndicatorAnimate?: boolean;
}

const SegmentedContext = React.createContext<SegmentedContextValue>({});

const useSegmentedContext = () => {
  const context = React.useContext(SegmentedContext);
  if (!context) {
    throw new Error(
      'SegmentedControl 컴포넌트는 SegmentedControl 내부에서 사용되어야 합니다.',
    );
  }
  return context;
};

export interface SegmentedControlProps extends React.ComponentProps<'div'> {
  /* 기본 세그먼트 컨트롤 속성 */
  defaultValue?: string;
  /* 현재 선택된 세그먼트 컨트롤의 value (제어된 컴포넌트) */
  value?: string;
  /* 세그먼트 컨트롤 값이 변경될 때 호출되는 함수 */
  onValueChange?: (value: string) => void;
  /* 세그먼트 컨트롤 크기 */
  size?: 'sm' | 'md' | 'lg';
  /* 세그먼트 컨트롤 컨텐츠 렌더링 방식 설정 true: display: none (컴포넌트 언마운트 안됨), false: 조건부 렌더링 (컴포넌트 언마운트 됨) */
  keepMounted?: boolean;
  /* 세그먼트 배경 인디케이터 표시 여부 */
  isShowBgIndicator?: boolean;
  /* 세그먼트 배경 인디케이터 애니메이션 여부 */
  isBgIndicatorAnimate?: boolean;
}

function Segmented({
  defaultValue,
  value,
  onValueChange,
  size = 'md',
  keepMounted = false,
  isShowBgIndicator = true,
  isBgIndicatorAnimate = true,
  className,
  ...props
}: SegmentedControlProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || '');
  const [segmentElements, setSegmentElements] = React.useState<
    Map<string, HTMLElement>
  >(new Map());

  const currentValue = value !== undefined ? value : internalValue;
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [value, onValueChange],
  );

  const registerSegment = React.useCallback(
    (value: string, element: HTMLElement) => {
      setSegmentElements((prev) => new Map(prev.set(value, element)));
    },
    [],
  );

  const unregisterSegment = React.useCallback((value: string) => {
    setSegmentElements((prev) => {
      const newMap = new Map(prev);
      newMap.delete(value);
      return newMap;
    });
  }, []);

  const contextValue = React.useMemo(
    () => ({
      value: currentValue,
      size,
      onValueChange: handleValueChange,
      keepMounted,
      registerSegment,
      unregisterSegment,
      segmentElements,
      isShowBgIndicator,
      isBgIndicatorAnimate,
    }),
    [
      currentValue,
      handleValueChange,
      keepMounted,
      size,
      registerSegment,
      unregisterSegment,
      segmentElements,
      isShowBgIndicator,
      isBgIndicatorAnimate,
    ],
  );

  return (
    <SegmentedContext.Provider value={contextValue}>
      <div
        data-slot="segmented-control"
        data-size={size}
        className={cn('group w-full', className)}
        {...props}
      />
    </SegmentedContext.Provider>
  );
}

export interface SegmentedContainerProps extends React.ComponentProps<'div'> {
  /* 세그먼트 컨테이너 배경 색상 표시 여부 */
  isBgColor?: boolean;
}

function SegmentedContainer({
  children,
  className,
  isBgColor = true,
  ...props
}: SegmentedContainerProps) {
  const {
    value: selectedValue,
    segmentElements,
    isShowBgIndicator,
    isBgIndicatorAnimate,
    size,
  } = useSegmentedContext();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [resizeRevision, setResizeRevision] = React.useState(0);
  const [indicatorStyle, setIndicatorStyle] = React.useState<{
    width: number;
    height: number;
    left: number;
    top: number;
    opacity: number;
  }>({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    opacity: 0,
  });
  const [shouldAnimateIndicator, setShouldAnimateIndicator] =
    React.useState(false);
  const hasInitialIndicatorPositionRef = React.useRef(false);

  // size에 따른 고정 높이값 정의
  const getHeightBySize = (size?: string) => {
    switch (size) {
      case 'sm':
        return 24;
      case 'lg':
        return 40;
      case 'md':
      default:
        return 32;
    }
  };

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      setResizeRevision((current) => current + 1);
    });
    observer.observe(container);
    const selectedElement = selectedValue
      ? segmentElements?.get(selectedValue)
      : undefined;
    if (selectedElement) {
      observer.observe(selectedElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [segmentElements, selectedValue]);

  React.useLayoutEffect(() => {
    if (
      !selectedValue ||
      !segmentElements ||
      !containerRef.current ||
      !isShowBgIndicator
    ) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      setShouldAnimateIndicator(false);
      hasInitialIndicatorPositionRef.current = false;
      return;
    }

    const selectedElement = segmentElements.get(selectedValue);
    if (!selectedElement) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      setShouldAnimateIndicator(false);
      hasInitialIndicatorPositionRef.current = false;
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const segmentRect = selectedElement.getBoundingClientRect();

    // 컨테이너의 패딩 값 계산 (p-4xs)
    const containerStyle = getComputedStyle(containerRef.current);
    const paddingLeft = parseFloat(containerStyle.paddingLeft);
    const paddingTop = parseFloat(containerStyle.paddingTop);

    setIndicatorStyle({
      width: segmentRect.width,
      height: getHeightBySize(size), // size에 따른 고정 높이값 사용
      left: segmentRect.left - containerRect.left - paddingLeft,
      top: segmentRect.top - containerRect.top - paddingTop,
      opacity: 1,
    });

    // 첫 위치 계산 시점에는 애니메이션 없이 정확한 위치로 배치하고,
    // 다음 프레임부터 전환 애니메이션을 활성화한다.
    if (!hasInitialIndicatorPositionRef.current) {
      hasInitialIndicatorPositionRef.current = true;
      if (!isBgIndicatorAnimate) return;

      const raf = window.requestAnimationFrame(() => {
        setShouldAnimateIndicator(true);
      });

      return () => {
        window.cancelAnimationFrame(raf);
      };
    }
  }, [
    selectedValue,
    segmentElements,
    isShowBgIndicator,
    size,
    isBgIndicatorAnimate,
    resizeRevision,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        `relative flex w-full gap-4xs rounded-xs`,
        isBgColor && 'bg-sys-bg-surface-sunken p-4xs',
        className,
      )}
      {...props}
    >
      {children}
      {/* Animated background indicator */}
      {isShowBgIndicator && (
        <div
          className={cn(
            'absolute rounded-xs bg-sys-bg-surface-1-selected inset-shadow-basic-md transition-[transform,width,opacity] ease-out',
            isBgIndicatorAnimate && shouldAnimateIndicator
              ? 'duration-200'
              : 'duration-0',
          )}
          style={{
            width: `${indicatorStyle.width}px`,
            height: `${indicatorStyle.height}px`,
            transform: `translate(${indicatorStyle.left}px, ${indicatorStyle.top}px)`,
            opacity: indicatorStyle.opacity,
          }}
        />
      )}
    </div>
  );
}

const segmentedItemVariants = cva(
  [
    'relative flex items-center justify-center rounded-xs w-full transition-all cursor-pointer z-10',
    'text-sys-cont-neutral-lightest [&_path]:fill-sys-cont-neutral-lightest',
    '[&_path]:transition-all',
  ],
  {
    variants: {
      size: {
        sm: 'text-comp-accent-xsmall! h-[24px] px-2xs',
        md: 'text-comp-accent-small! h-[32px] px-xs',
        lg: 'text-comp-accent-medium! h-[40px] px-sm',
      },
      isSelected: {
        true: 'text-sys-cont-neutral-default [&_path]:fill-sys-cont-neutral-default',
        false:
          'hover:text-sys-cont-neutral-light hover:[&_path]:fill-sys-cont-neutral-light',
      },
      isDisabled: {
        // disabled-lightest 는 트랙 배경과 명도가 거의 같아 글자가 사라져 보인다.
        // disabled 계열 중 가장 진한 default 를 써서 읽히되 비활성으로 인지되게 한다.
        true: [
          'text-sys-cont-neutral-disabled-default cursor-not-allowed [&_path]:fill-sys-cont-neutral-disabled-default',
          'hover:text-sys-cont-neutral-disabled-default hover:[&_path]:fill-sys-cont-neutral-disabled-default',
        ],
        false: '',
      },
    },
  },
);

export interface SegmentedItemProps extends React.ComponentProps<'div'> {
  value: string;
  isDisabled?: boolean;
}

function SegmentedItem({
  value,
  className,
  children,
  isDisabled,
  ...props
}: SegmentedItemProps) {
  const {
    size,
    value: selectedValue,
    onValueChange,
    registerSegment,
    unregisterSegment,
  } = useSegmentedContext();
  const segmentRef = React.useRef<HTMLDivElement>(null);
  const isSelected = selectedValue === value;

  React.useEffect(() => {
    const element = segmentRef.current;
    if (element && registerSegment) {
      registerSegment(value, element);
    }

    return () => {
      if (unregisterSegment) {
        unregisterSegment(value);
      }
    };
  }, [value, registerSegment, unregisterSegment]);

  return (
    <div
      ref={segmentRef}
      data-slot="segmented-item"
      data-value={value}
      className={cn(
        segmentedItemVariants({ size, isSelected, isDisabled }),
        className,
      )}
      onClick={() => !isDisabled && onValueChange?.(value)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface SegmentedContentProps extends React.ComponentProps<'div'> {
  value: string;
}

function SegmentedContent({
  value,
  children,
  className,
  ...props
}: SegmentedContentProps) {
  const { value: selectedValue, keepMounted } = useSegmentedContext();
  const isSelected = selectedValue === value;

  if (!keepMounted && !isSelected) {
    return null;
  }

  return (
    <div
      data-slot="segmented-content"
      data-value={value}
      className={cn(
        'flex w-full',
        isSelected && 'animate-in fade-in duration-150',
        className,
      )}
      style={{
        display: keepMounted && !isSelected ? 'none' : undefined,
        ...props.style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export { Segmented, SegmentedContainer, SegmentedItem, SegmentedContent };
